// frontend/src/context/AuthContext.js
import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
} from "react";
import api from "../services/api";
import toast from "react-hot-toast";
import { getCurrentTenantCode } from "../utils/tenant";

const AuthContext = createContext();
const SESSION_ACTIVITY_CHECK_MS = 15 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token") || null);
  const [loading, setLoading] = useState(true);
  const [sessionValid, setSessionValid] = useState(false);
  const sessionIntervalRef = useRef(null);
  const tokenCheckIntervalRef = useRef(null);
  const sessionExpiryTimeoutRef = useRef(null);
  const sessionActivityCleanupRef = useRef(null);
  const lastActivityRef = useRef(0);
  const sessionExpiryHandledRef = useRef(false);

  const persistLastUser = (userData) => {
    if (!userData?.email) return;
    localStorage.setItem(
      "lastUser",
      JSON.stringify({
        email: userData.email,
        name: userData.name || userData.userName || "",
        provider:
          userData.provider || (userData.isSocialLogin ? "google" : "password"),
      }),
    );
  };

  const isProtectedSessionPath = (pathname = window.location.pathname) =>
    pathname === "/checkout" ||
    pathname === "/dashboard" ||
    pathname.startsWith("/admin");

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem("token");
      const storedUser = localStorage.getItem("user");
      const tokenExpiry = localStorage.getItem("tokenExpiry");

      if (storedToken && storedUser) {
        try {
          // Check if token is expired
          if (tokenExpiry && new Date().getTime() > parseInt(tokenExpiry)) {
            console.log("Token expired, handling session expiry");
            handleSessionExpired();
            setLoading(false);
            return;
          }

          const userData = JSON.parse(storedUser);
          setUser(userData);
          setToken(storedToken);

          // Validate session
          const sessionToken = localStorage.getItem("sessionToken");
          if (sessionToken) {
            try {
              const response = await api.post("/auth/validate-session", {
                sessionToken,
              });
              if (response.data.success && response.data.valid) {
                setSessionValid(true);
                startSessionActivityTracking(
                  sessionToken,
                  localStorage.getItem("sessionExpiry"),
                );
              } else {
                console.log("Session invalid, handling session expiry");
                handleSessionExpired();
              }
            } catch (error) {
              console.error("Session validation failed:", error);
              // Don't logout immediately, might be network issue
            }
          }
        } catch (error) {
          console.error("Error initializing auth:", error);
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          localStorage.removeItem("sessionToken");
          setUser(null);
          setToken(null);
        }
      }
      setLoading(false);
    };

    initializeAuth();

    // Start token expiry checker
    startTokenExpiryChecker();

    const handleAppReturn = () => {
      const sessionExpiry = localStorage.getItem("sessionExpiry");
      if (sessionExpiry && Date.now() >= new Date(sessionExpiry).getTime()) {
        handleSessionExpired();
      }
    };

    const handleApiSessionExpired = (event) => {
      handleSessionExpired(event.detail?.message);
    };

    window.addEventListener("focus", handleAppReturn);
    window.addEventListener("pageshow", handleAppReturn);
    window.addEventListener("auth-session-expired", handleApiSessionExpired);
    document.addEventListener("visibilitychange", handleAppReturn);

    // Cleanup on unmount
    return () => {
      if (sessionIntervalRef.current) {
        clearInterval(sessionIntervalRef.current);
      }
      if (sessionExpiryTimeoutRef.current) {
        clearTimeout(sessionExpiryTimeoutRef.current);
      }
      sessionActivityCleanupRef.current?.();
      if (tokenCheckIntervalRef.current) {
        clearInterval(tokenCheckIntervalRef.current);
      }
      window.removeEventListener("focus", handleAppReturn);
      window.removeEventListener("pageshow", handleAppReturn);
      window.removeEventListener(
        "auth-session-expired",
        handleApiSessionExpired,
      );
      document.removeEventListener("visibilitychange", handleAppReturn);
    };
  }, []);

  const scheduleSessionExpiry = (sessionExpiry) => {
    if (sessionExpiryTimeoutRef.current) {
      clearTimeout(sessionExpiryTimeoutRef.current);
    }

    const expiryTime = new Date(sessionExpiry).getTime();
    if (!Number.isFinite(expiryTime)) return;

    const delay = Math.max(0, expiryTime - Date.now());
    sessionExpiryTimeoutRef.current = setTimeout(() => {
      handleSessionExpired(
        "Your session expired after a period of inactivity.",
      );
    }, delay);
  };

  // Renew the idle timeout only after real user activity, not on a timer.
  const startSessionActivityTracking = (sessionToken, sessionExpiry) => {
    sessionActivityCleanupRef.current?.();
    scheduleSessionExpiry(
      sessionExpiry || localStorage.getItem("sessionExpiry"),
    );
    lastActivityRef.current = Date.now();

    const handleActivity = async () => {
      const now = Date.now();
      if (now - lastActivityRef.current < SESSION_ACTIVITY_CHECK_MS) return;
      lastActivityRef.current = now;

      try {
        const response = await api.post("/auth/extend-session", {
          sessionToken,
        });
        if (response.data.success && response.data.sessionExpiry) {
          localStorage.setItem("sessionExpiry", response.data.sessionExpiry);
          scheduleSessionExpiry(response.data.sessionExpiry);
        }
      } catch (error) {
        if (error.response?.status === 401) {
          handleSessionExpired("Your session expired. Please log in again.");
        }
      }
    };

    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((eventName) =>
      window.addEventListener(eventName, handleActivity, { passive: true }),
    );

    sessionActivityCleanupRef.current = () => {
      events.forEach((eventName) =>
        window.removeEventListener(eventName, handleActivity),
      );
    };
  };

  // Start token expiry checker
  const startTokenExpiryChecker = () => {
    if (tokenCheckIntervalRef.current) {
      clearInterval(tokenCheckIntervalRef.current);
    }

    tokenCheckIntervalRef.current = setInterval(() => {
      const tokenExpiry = localStorage.getItem("tokenExpiry");
      if (tokenExpiry) {
        const expiryTime = parseInt(tokenExpiry);
        const now = new Date().getTime();
        // If token expires in less than 5 minutes, refresh it
        if (expiryTime - now < 5 * 60 * 1000) {
          console.log("Token expiring soon, refreshing...");
          refreshToken();
        }
        // If token is expired, logout
        if (now > expiryTime) {
          console.log("Token expired, logging out");
          handleSessionExpired();
        }
      }
    }, 60 * 1000); // Check every minute
  };

  // Refresh token
  const refreshToken = async () => {
    try {
      const currentToken = localStorage.getItem("token");
      if (!currentToken) return;

      const response = await api.post("/auth/refresh-token", null, {
        headers: { Authorization: `Bearer ${currentToken}` },
      });

      if (response.data.success && response.data.token) {
        const newToken = response.data.token;
        localStorage.setItem("token", newToken);
        setToken(newToken);

        const newExpiry = new Date().getTime() + 7 * 24 * 60 * 60 * 1000;
        localStorage.setItem("tokenExpiry", newExpiry.toString());

        console.log("Token refreshed successfully");
      }
    } catch (error) {
      console.error("Failed to refresh token:", error);
      // If refresh fails, logout
      if (error.response?.status === 401) {
        handleSessionExpired();
      }
    }
  };

  // ==================== EMAIL VERIFICATION FUNCTIONS ====================

  // Resend verification email
  const resendVerification = async (
    email = null,
    { silent = false } = {},
  ) => {
    try {
      const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
      const tenantCode =
        localStorage.getItem("tenantCode") || currentUser?.tenantCode;
      const verificationEmail =
        email || currentUser?.email || user?.email ||
        localStorage.getItem("pendingVerificationEmail");

      const response = await api.post("/auth/resend-verification", {
        email: verificationEmail,
        tenantCode: tenantCode,
      });

      if (response.data.success) {
        if (!silent) {
          toast.success(
            "Verification email sent successfully! Please check your inbox.",
          );
        }
        return { success: true };
      }

      const message = response.data.message || "Failed to resend verification";
      if (!silent) toast.error(message);
      return { success: false, message };
    } catch (error) {
      console.error("Resend verification error:", error);
      const message =
        error.response?.data?.message ||
        "Failed to resend verification. Please try again.";
      if (!silent) toast.error(message);
      return { success: false, message };
    }
  };

  // Refresh user data from API
  const refreshUser = async () => {
    try {
      const response = await api.get("/api/auth/me");
      if (response.data) {
        const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
        const updatedUser = {
          ...currentUser,
          ...response.data,
          emailVerified: response.data.emailVerified === true,
        };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        setUser(updatedUser);
        return { success: true, user: updatedUser };
      }
      return { success: false };
    } catch (error) {
      console.error("Refresh user error:", error);
      return { success: false };
    }
  };

  // ==================== AUTHENTICATION FUNCTIONS ====================

  const completeLogin = (
    authData,
    { preserveGuestCart = false } = {},
  ) => {
    const { token, user, sessionToken, sessionExpiry } = authData || {};
    if (!token || !user) {
      return { success: false, message: "Invalid authentication response" };
    }

    sessionExpiryHandledRef.current = false;
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(user));
    if (sessionToken) {
      localStorage.setItem("sessionToken", sessionToken);
    }
    if (sessionExpiry) {
      localStorage.setItem("sessionExpiry", sessionExpiry);
    }
    if (user.tenantCode) {
      localStorage.setItem("tenantCode", user.tenantCode);
    }

    const expiry = Date.now() + 7 * 24 * 60 * 60 * 1000;
    localStorage.setItem("tokenExpiry", expiry.toString());
    localStorage.removeItem("pendingVerificationEmail");
    if (preserveGuestCart) {
      localStorage.setItem("mergeGuestCartAfterLogin", "true");
    }

    setUser(user);
    setToken(token);
    setSessionValid(true);
    persistLastUser(user);

    if (sessionToken) {
      startSessionActivityTracking(sessionToken, sessionExpiry);
    }

    return { success: true, user };
  };

  // Login function
  const login = async (email, password) => {
    try {
      setLoading(true);
      const response = await api.post("/auth/login", {
        email,
        password,
        tenantCode: getCurrentTenantCode(),
      });

      if (response.data.success) {
        const loginResult = completeLogin(response.data, {
          preserveGuestCart: true,
        });
        if (!loginResult.success) return loginResult;

        toast.success(
          `Welcome back, ${loginResult.user.name || loginResult.user.email}!`,
        );
        return loginResult;
      }
      return {
        success: false,
        message: response.data.message || "Login failed",
      };
    } catch (error) {
      console.error("Login error:", error);
      const message =
        error.response?.data?.message || "Login failed. Please try again.";
      toast.error(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      setLoading(true);
      const response = await api.post("/auth/register", {
        ...userData,
        tenantCode: userData.tenantCode || getCurrentTenantCode(),
      });

      if (response.data.success) {
        const tenantCode =
          response.data.user?.tenantCode ||
          userData.tenantCode ||
          getCurrentTenantCode();
        if (tenantCode) {
          localStorage.setItem("tenantCode", tenantCode);
        }
        localStorage.setItem(
          "pendingVerificationEmail",
          userData.email || "",
        );
        return {
          success: true,
          requiresVerification: true,
          user: response.data.user,
        };
      }
      return {
        success: false,
        message: response.data.message || "Registration failed",
      };
    } catch (error) {
      console.error("Registration error:", error);
      const message =
        error.response?.data?.message ||
        "Registration failed. Please try again.";
      toast.error(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async (
    reason = "Logged out successfully",
    { clearLastUser = false } = {},
  ) => {
    try {
      const sessionToken = localStorage.getItem("sessionToken");
      if (sessionToken) {
        await api.post("/auth/logout", { sessionToken });
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Clear local storage
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("sessionToken");
      localStorage.removeItem("sessionExpiry");
      localStorage.removeItem("tokenExpiry");
      if (clearLastUser) {
        localStorage.removeItem("lastUser");
      }

      // Clear state
      setUser(null);
      setToken(null);
      setSessionValid(false);

      // Clear intervals
      if (sessionIntervalRef.current) {
        clearInterval(sessionIntervalRef.current);
        sessionIntervalRef.current = null;
      }
      if (sessionExpiryTimeoutRef.current) {
        clearTimeout(sessionExpiryTimeoutRef.current);
        sessionExpiryTimeoutRef.current = null;
      }
      sessionActivityCleanupRef.current?.();
      sessionActivityCleanupRef.current = null;

      toast.success(reason);
    }
  };

  const handleSessionExpired = (
    message = "Your session expired. Please sign in again.",
  ) => {
    if (sessionExpiryHandledRef.current) return;
    sessionExpiryHandledRef.current = true;

    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("sessionToken");
    localStorage.removeItem("sessionExpiry");
    localStorage.removeItem("tokenExpiry");
    sessionStorage.setItem("sessionExpired", "1");

    setUser(null);
    setToken(null);
    setSessionValid(false);
    if (sessionExpiryTimeoutRef.current) {
      clearTimeout(sessionExpiryTimeoutRef.current);
      sessionExpiryTimeoutRef.current = null;
    }
    sessionActivityCleanupRef.current?.();
    sessionActivityCleanupRef.current = null;
    if (isProtectedSessionPath()) {
      sessionStorage.setItem("sessionExpired", "1");
      window.location.replace("/login?expired=1&t=" + Date.now());
    } else {
      window.dispatchEvent(
        new CustomEvent("auth-signed-out", {
          detail: {
            message: "Your session expired. You are browsing as a guest.",
          },
        }),
      );
    }
  };

  // Update user profile
  const updateProfile = async (profileData) => {
    try {
      const response = await api.put("/auth/profile", profileData);
      if (response.data.success) {
        const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
        const updatedUser = { ...currentUser, ...profileData };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        setUser(updatedUser);
        toast.success("Profile updated successfully");
        return { success: true };
      }
      return {
        success: false,
        message: response.data.message || "Update failed",
      };
    } catch (error) {
      console.error("Update profile error:", error);
      const message =
        error.response?.data?.message || "Update failed. Please try again.";
      toast.error(message);
      return { success: false, message };
    }
  };

  // Change password
  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await api.put("/auth/change-password", {
        currentPassword,
        newPassword,
      });
      if (response.data.success) {
        toast.success("Password changed successfully");
        return { success: true };
      }
      return {
        success: false,
        message: response.data.message || "Change failed",
      };
    } catch (error) {
      console.error("Change password error:", error);
      const message =
        error.response?.data?.message || "Change failed. Please try again.";
      toast.error(message);
      return { success: false, message };
    }
  };

  // Delete account
  const deleteAccount = async () => {
    try {
      const response = await api.delete("/auth/account");
      if (response.data.success) {
        await logout("Account deactivated successfully", {
          clearLastUser: true,
        });
        toast.success("Account deactivated successfully");
        return { success: true };
      }
      return {
        success: false,
        message: response.data.message || "Deletion failed",
      };
    } catch (error) {
      console.error("Delete account error:", error);
      const message =
        error.response?.data?.message || "Deletion failed. Please try again.";
      toast.error(message);
      return { success: false, message };
    }
  };

  // Forgot password
  const forgotPassword = async (email) => {
    try {
      const response = await api.post("/auth/forgot-password", { email });
      if (response.data.success) {
        toast.success("Password reset email sent");
        return { success: true };
      }
      return {
        success: false,
        message: response.data.message || "Request failed",
      };
    } catch (error) {
      console.error("Forgot password error:", error);
      const message =
        error.response?.data?.message || "Request failed. Please try again.";
      toast.error(message);
      return { success: false, message };
    }
  };

  // Reset password
  const resetPassword = async (token, newPassword) => {
    try {
      const response = await api.post("/auth/reset-password", {
        token,
        newPassword,
      });
      if (response.data.success) {
        toast.success("Password reset successfully");
        return { success: true };
      }
      return {
        success: false,
        message: response.data.message || "Reset failed",
      };
    } catch (error) {
      console.error("Reset password error:", error);
      const message =
        error.response?.data?.message || "Reset failed. Please try again.";
      toast.error(message);
      return { success: false, message };
    }
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    const token = localStorage.getItem("token");
    const tokenExpiry = localStorage.getItem("tokenExpiry");

    if (!token) return false;
    if (tokenExpiry && new Date().getTime() > parseInt(tokenExpiry)) {
      return false;
    }
    return !!user;
  };

  // Get user role
  const getUserRole = () => {
    return user?.role || "Customer";
  };

  // Check if user is admin
  const isAdmin = () => {
    const role = getUserRole();
    return role === "Admin" || role === "SuperAdmin" || role === "TenantAdmin";
  };

  // Check if user is super admin
  const isSuperAdmin = () => {
    return getUserRole() === "SuperAdmin";
  };

  // ==================== CONTEXT VALUE ====================

  const value = {
    // State
    user,
    setUser,
    token,
    setToken,
    loading,
    sessionValid,

    // Authentication functions
    login,
    completeLogin,
    register,
    logout,
    handleSessionExpired,

    // Profile functions
    updateProfile,
    changePassword,
    deleteAccount,

    // Password reset functions
    forgotPassword,
    resetPassword,

    // Email verification functions (ADDED)
    resendVerification,
    refreshUser,

    // Session functions
    refreshToken,

    // Helper functions
    isAuthenticated,
    getUserRole,
    isAdmin,
    isSuperAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
